import React, { useEffect, useState } from "react";
import Wordmark from "../components/Wordmark.jsx";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";

/**
 * Share entry — what a link visitor lands on.
 *
 * The common case is someone with no account: a respite worker opening this on
 * a phone in a car park ten minutes before a shift. For them there is nothing
 * to read, nothing to agree to, and no account to make — it opens, or it plainly
 * says it can't.
 *
 * The awkward case is someone who IS signed in. Opening a link replaces their
 * session, because holding two identities at once is how confused-deputy bugs
 * start. So when someone is signed in we ask first, via /check, which mutates
 * nothing — no session, no access count, no timestamp.
 *
 * PREVIEW IS NOT A SESSION CHANGE. An owner asking "what will Amy see?" is
 * asking a RENDERING question, not an authentication one — she already has view
 * access to this client. Swapping her to an anonymous visitor to answer it was
 * a mistake: browser cookies are shared across every tab in a profile, so the
 * swap silently broke whatever she had open elsewhere, and closing the preview
 * tab did not put it back. Preview now just marks the tab and renders the
 * profile with her own session, which is both safer and reversible.
 *
 * Visitors WITHOUT access still swap for real, because for them it is genuinely
 * an identity change rather than a view of one.
 */
export default function ShareEntry() {
  const { token }   = useParams();
  const navigate    = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("checking");   // checking | choosing | opening | error
  const [info, setInfo]   = useState(null);
  const [error, setError] = useState(null);

  // Cosmetic preview: mark this tab and render normally. sessionStorage is
  // per-tab, so a preview in one tab never affects another.
  function startPreview() {
    try {
      sessionStorage.setItem("cg_preview_client", info.client.id);
      sessionStorage.setItem("cg_preview_name", info.client.display_name || "");
    } catch { /* private mode with storage disabled — banner just won't show */ }
    navigate(`/clients/${info.client.id}`, { replace: true });
  }

  async function open() {
    setState("opening");
    try {
      const r = await fetch(`/api/share/${token}/open`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "This link isn't available anymore.");
      await refresh();
      navigate(`/clients/${d.client.id}`, { replace: true });
    } catch (e) {
      setError(e.message);
      setState("error");
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/share/${token}/check`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "This link isn't available anymore.");
        if (cancelled) return;
        setInfo(d);
        if (!d.signed_in_as) { open(); return; }   // nobody signed in — straight in
        setState("choosing");
      } catch (e) {
        if (!cancelled) { setError(e.message); setState("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state === "error") {
    return (
      <Frame>
        <h1 style={s.heading}>This link isn't available</h1>
        <p style={s.body}>
          {error || "It may have expired, or the family may have turned it off."}{" "}
          Ask whoever shared it with you for a new one.
        </p>
      </Frame>
    );
  }

  if (state === "choosing" && info) {
    const name = info.client.display_name;
    return (
      <Frame>
        {info.has_access ? (
          <>
            <h1 style={s.heading}>You already have access</h1>
            <p style={s.body}>
              You're signed in as <strong>{info.signed_in_as}</strong>, and you
              can already see {name}'s profile in full.
            </p>
            <button style={s.primary}
                    onClick={() => navigate(`/clients/${info.client.id}`, { replace: true })}>
              Open {name}'s profile
            </button>
            <button style={s.secondary} onClick={startPreview}>
              Preview what your visitor sees
            </button>
            <p style={s.fine}>
              Preview stays signed in as you — nothing is logged out, and you can
              leave it at any time. To check the link itself end to end, open it
              in a private window instead.
            </p>
          </>
        ) : (
          <>
            <h1 style={s.heading}>Open this shared profile?</h1>
            <p style={s.body}>
              You're signed in as <strong>{info.signed_in_as}</strong>. Opening
              this link will sign you out and show you {name}'s profile as a
              read-only visitor.
            </p>
            <button style={s.primary} onClick={open}>Continue as a visitor</button>
            <button style={s.secondary} onClick={() => navigate("/", { replace: true })}>
              Cancel
            </button>
          </>
        )}
      </Frame>
    );
  }

  return <Frame><p style={s.body}>Opening…</p></Frame>;
}

function Frame({ children }) {
  return (
    <div style={s.page}>
      <div style={s.card}>
        <Wordmark />
        {children}
      </div>
    </div>
  );
}

const s = {
  page:      { minHeight: "100dvh", background: "var(--color-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 36px", width: "100%", maxWidth: 440, textAlign: "center" },
  wordmark:  { marginBottom: 24, fontSize: 20, fontWeight: "var(--weight-medium)", letterSpacing: "-0.4px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  dot:       { display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "var(--color-accent)", flexShrink: 0 },
  heading:   { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", marginBottom: 8 },
  body:      { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", marginBottom: 20 },
  primary:   { width: "100%", padding: "11px 16px", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer", font: "inherit", marginBottom: 10 },
  secondary: { width: "100%", padding: "10px 16px", background: "none", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", fontSize: "var(--text-sm)", cursor: "pointer", font: "inherit" },
  fine:      { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: "1.5", marginTop: 16 },
};
